// UpgradePlan.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom'; // Correctly imported for linking
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material'; // Added CircularProgress

// --- Configuration ---
const MAX_PLAN_ID = 4; // Make sure this matches your highest plan ID

export default function UpgradePlan() {
  const [subscriptionData, setSubscriptionData] = useState(null);
  // Separate loading state for initial load vs subsequent updates
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false); // For updates after initial load
  const [error, setError] = useState(null);

  // --- Fetch Logic (memoized) ---
  const fetchSubscriptionData = useCallback((isUpdate = false) => {
    console.log('UpgradePlan: Reading subscription data from localStorage...');
    if (isUpdate) {
      setIsUpdating(true); // Indicate update in progress
    } else {
      setIsInitialLoading(true); // Indicate initial load
    }
    setError(null);

    // Use setTimeout to allow UI to update before potentially blocking localStorage read
    setTimeout(() => {
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const userData = JSON.parse(userStr);
                // Check for the expected structure
                if (userData && userData.subscription && userData.subscription.plan) {
                    setSubscriptionData(userData.subscription);
                     console.log('UpgradePlan: Found subscription:', userData.subscription.plan.name);
                } else {
                    console.warn("UpgradePlan: Subscription data missing or incomplete in localStorage.");
                    // Default to Freemium if structure is wrong but user key exists
                    setSubscriptionData({ plan: { name: 'Freemium', id: null } });
                }
            } else {
                console.warn("UpgradePlan: User data not found in localStorage.");
                // Default to Freemium if user key doesn't exist
                setSubscriptionData({ plan: { name: 'Freemium', id: null } });
            }
        } catch (e) {
            console.error("UpgradePlan: Error parsing user data from localStorage:", e);
            setError("Could not load your subscription information.");
            setSubscriptionData(null); // Set to null on error
        } finally {
             if (isUpdate) {
               setIsUpdating(false);
             } else {
               setIsInitialLoading(false);
             }
        }
    }, 0); // setTimeout 0ms pushes execution to after current render cycle

  }, []); // No dependencies, function identity is stable

  // --- Effect for Initial Fetch and Listeners ---
  useEffect(() => {
    document.title = 'Your Plan | KlonIT Web Application';
    fetchSubscriptionData(false); // Initial load

    // --- Listener for SAME-TAB updates (Custom Event) ---
    const handleLocalStorageUpdate = (event) => {
      console.log('UpgradePlan: Detected custom userLocalStorageUpdated event.');
      // Optional: Check event detail if you pass data with the event
      // console.log('Event detail:', event.detail);
      fetchSubscriptionData(true); // Fetch as an update
    };
    window.addEventListener('userLocalStorageUpdated', handleLocalStorageUpdate);

    // --- Listener for CROSS-TAB updates (Storage Event) ---
    const handleStorageEvent = (event) => {
      // Check if the key changed is 'user' and it happened in another tab/window
      if (event.key === 'user' && event.storageArea === localStorage) {
        console.log('UpgradePlan: Detected storage event for "user" key from another tab.');
        fetchSubscriptionData(true); // Fetch as an update
      }
    };
    window.addEventListener('storage', handleStorageEvent);

    // --- Cleanup Function ---
    return () => {
      console.log('UpgradePlan: Cleaning up event listeners.');
      window.removeEventListener('userLocalStorageUpdated', handleLocalStorageUpdate);
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [fetchSubscriptionData]); // Depend on fetchSubscriptionData

  // --- Rendering Logic ---

  // Show full loading indicator only on initial load
  if (isInitialLoading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, minHeight: '150px' }}>
            <CircularProgress />
        </Box>
    );
  }

  // Show error message if an error occurred
  if (error) {
    return (
      <Paper elevation={1} sx={{ p: 2, m: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
        <Typography>Error: {error}</Typography>
      </Paper>
    );
  }

  // Handle case where data couldn't be loaded but no specific error was set
  // (Should ideally be caught by error handling, but as a fallback)
  if (!subscriptionData || !subscriptionData.plan) {
       console.warn("UpgradePlan: subscriptionData or subscriptionData.plan is null/undefined during render.");
      // You might want a more specific message or fallback UI here
      return (
          <Paper elevation={1} sx={{ p: 2, m: 2 }}>
              <Typography>Could not determine your current plan.</Typography>
          </Paper>
      );
  }

  // Safely access plan details now that we've checked subscriptionData.plan
  let  currentPlanName = subscriptionData?.plan?.name|| 'Freemium';
  currentPlanName = currentPlanName.charAt(0).toUpperCase() + currentPlanName.slice(1)

  const currentPlanId = subscriptionData?.plan?.id || 0;

  const isOnMaxPlan = currentPlanId !== null && currentPlanId === MAX_PLAN_ID;

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={2} // Slightly less prominent elevation
        sx={{
          p: 3,
          textAlign: 'center',
          borderRadius: 2,
          // Use theme palette for consistency
          background: (theme) => theme.palette.background.paper,
          position: 'relative', // Needed for absolute positioning of loader
          minHeight: '150px', // Ensure minimum height
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center', // Center content vertically
          alignItems: 'center' // Center content horizontally
        }}
      >
        {/* Show subtle loading indicator during updates */}
        {isUpdating && (
            <CircularProgress size={24} sx={{ position: 'absolute', top: 16, right: 16 }} />
        )}

        <Typography variant="h6" gutterBottom component="div"> {/* Use component div */}
          Your Current Plan: <strong style={{ color: (theme) => theme.palette.primary.main }}>{currentPlanName}</strong>
        </Typography>

        {isOnMaxPlan ? (
          <Typography variant="body1" sx={{ mt: 2 }}> {/* Use body1 for slightly larger text */}
            You are already on our highest available plan. Thank you!
          </Typography>
        ) : (
          <>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}> {/* Add margin */}
              {currentPlanName === 'Freemium'
                ? 'Upgrade to unlock premium features and benefits!'
                : `Explore higher tiers for even more capabilities.`}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              component={Link} // Use Link component for client-side routing
              to="/billing?tab=plan" // Correct path with query parameter
              sx={{ mt: 1, minWidth: '200px' }} // Ensure button isn't too small
              disabled={isUpdating} // Disable button only during updates
            >
              {isUpdating ? 'Loading...' : 'View Plans & Upgrade'}
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}